<template>
  <section v-if="loading || items.length" class="home-products">
    <div class="container">
      <SectionHeader
        :label="config.label || (kind === 'course' ? 'الكورسات' : 'السنوات الدراسية')"
        :title="config.title || (kind === 'course' ? 'أحدث الكورسات' : 'السنوات الدراسية المتاحة')"
        :subtitle="config.subtitle || (kind === 'course' ? 'اختر الكورس المناسب لك وابدأ التعلّم فوراً' : 'اختر سنتك الدراسية واشترك للوصول إلى كل المحتوى')"
      />

      <!-- Loading skeletons -->
      <div v-if="loading" class="hp-grid">
        <div v-for="n in 3" :key="n" class="hp-skel"></div>
      </div>

      <!-- Cards -->
      <div v-else class="hp-grid">
        <article v-for="p in items" :key="p.id" class="hp-card" @click="open(p)">
          <div class="hp-card__media">
            <img v-if="cover(p)" :src="cover(p)" :alt="name(p)" loading="lazy" />
            <div v-else class="hp-card__media--empty"><i class="pi" :class="kind === 'course' ? 'pi-book' : 'pi-graduation-cap'"></i></div>
            <span v-if="isOwnYear(p)" class="hp-card__own"><i class="pi pi-star-fill"></i> سنتك الدراسية</span>
          </div>
          <div class="hp-card__body">
            <span v-if="kind === 'course' && p.academic_year" class="hp-card__year">
              <i class="pi pi-graduation-cap"></i> {{ p.academic_year.name }}
            </span>
            <h3 class="hp-card__title">{{ name(p) }}</h3>
            <p v-if="p.description" class="hp-card__desc">{{ p.description }}</p>
            <div class="hp-card__foot">
              <div class="hp-card__price">
                <template v-if="isFree(p)">
                  <span class="hp-card__price--free"><i class="pi pi-gift"></i> مجاني</span>
                </template>
                <template v-else-if="hasPrice(p)">
                  <small>يبدأ من</small> {{ money(startingPrice(p)) }}
                </template>
                <span v-else class="hp-card__price--none">تواصل لمعرفة السعر</span>
              </div>
              <button class="hp-card__cta" @click.stop="open(p)">
                {{ ctaLabel(p) }} <i class="pi pi-arrow-left"></i>
              </button>
            </div>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
import SectionHeader from '@/components/common/SectionHeader.vue';
import { subscriptionService } from '@/services/subscriptionService.js';
import { productPlans, formatMoney } from '@/composables/subscriptionMeta.js';

export default {
  name: 'HomeProductsSection',
  components: { SectionHeader },
  props: {
    kind: { type: String, default: 'course' }, // 'course' | 'academic_year'
    config: { type: Object, default: () => ({}) },
  },
  data() {
    return { items: [], loading: true };
  },
  methods: {
    name(p) { return this.kind === 'course' ? p.title : p.name; },
    cover(p) { return p.cover_image_link || p.image_link || null; },
    money(v) { return formatMoney(v); },
    isFree(p) { return !p.is_subscription_enabled; },
    isOwnYear(p) { return this.kind === 'academic_year' && !!p.is_students_year; },
    ctaLabel(p) {
      if (p.has_access) return 'ادخل الآن';
      if (this.isFree(p)) return 'ابدأ الآن';
      return 'اشترك الآن';
    },
    startingPrice(p) {
      if (p.starting_price !== undefined && p.starting_price !== null) return Number(p.starting_price);
      const prices = productPlans(p).map(pl => pl.price);
      return prices.length ? Math.min(...prices) : null;
    },
    hasPrice(p) {
      const price = this.startingPrice(p);
      return price !== null && Number(price) > 0;
    },
    open(p) {
      this.$router.push(this.kind === 'course' ? `/course/${p.id}` : `/academic-year/${p.id}`);
    },
    async load() {
      this.loading = true;
      try {
        this.items = this.kind === 'course'
          ? await subscriptionService.homeCourses()
          : await subscriptionService.homeAcademicYears();
      } catch {
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() { this.load(); },
};
</script>

<style scoped>
.home-products { padding: 3rem 0; }
.hp-grid {
  display: grid; gap: 1.5rem;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
}
.hp-card {
  background: #fff; border: 1px solid rgba(91,95,239,.1); border-radius: 18px; overflow: hidden;
  cursor: pointer; transition: transform .2s ease, box-shadow .2s ease;
  display: flex; flex-direction: column;
}
.hp-card:hover { transform: translateY(-5px); box-shadow: 0 14px 34px rgba(91,95,239,.15); }
.hp-card__media { position: relative; aspect-ratio: 16 / 9; background: #eef2ff; }
.hp-card__media img { width: 100%; height: 100%; object-fit: cover; display: block; }
.hp-card__media--empty { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #5B5FEF; font-size: 2.4rem; }
.hp-card__own {
  position: absolute; top: 10px; inset-inline-start: 10px;
  background: rgba(245,158,11,.95); color: #1f2937;
  font-size: .72rem; font-weight: 800; padding: .25rem .6rem; border-radius: 99px;
  display: inline-flex; align-items: center; gap: .3rem;
}
.hp-card__body { padding: 1.1rem 1.2rem 1.2rem; flex: 1; display: flex; flex-direction: column; }
.hp-card__year {
  display: inline-flex; align-items: center; gap: .3rem; width: fit-content;
  background: rgba(91,95,239,.08); color: #5B5FEF; font-size: .72rem; font-weight: 700;
  padding: .25rem .6rem; border-radius: 99px; margin-bottom: .5rem;
}
.hp-card__title { font-size: 1.1rem; font-weight: 800; color: #111827; margin: 0 0 .5rem; }
.hp-card__desc {
  font-size: .88rem; color: #6B7280; line-height: 1.7; margin: 0 0 1rem;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.hp-card__foot { margin-top: auto; display: flex; align-items: center; justify-content: space-between; gap: .5rem; }
.hp-card__price { font-weight: 800; color: #5B5FEF; font-size: 1.05rem; }
.hp-card__price small { display: block; font-size: .68rem; color: #94a3b8; font-weight: 600; }
.hp-card__price--none { color: #94a3b8; font-weight: 700; }
.hp-card__price--free { color: #16a34a; display: inline-flex; align-items: center; gap: .3rem; font-size: .95rem; }
.hp-card__cta {
  background: linear-gradient(90deg, #5B5FEF, #7c4dff); color: #fff; border: none; border-radius: 10px;
  padding: .5rem .9rem; font-weight: 700; font-size: .82rem; cursor: pointer; white-space: nowrap;
  display: inline-flex; align-items: center; gap: .35rem;
}
.hp-skel {
  height: 300px; border-radius: 18px; background: #eef1f7; position: relative; overflow: hidden;
}
.hp-skel::after {
  content: ''; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent);
  animation: hp-sh 1.3s infinite;
}
@keyframes hp-sh { 100% { transform: translateX(100%); } }
@media (max-width: 600px) {
  .hp-card__cta { padding: .45rem .7rem; font-size: .74rem; }
}

/* ── Mobile app home: compact header + horizontal snap carousel ── */
@media (max-width: 768px) {
  .home-products { padding: 1rem 0 .25rem; }
  .home-products .container { padding: 0 1rem; }
  .home-products :deep(.section-header) { margin-bottom: .85rem; }
  .home-products :deep(.section-label) { font-size: .7rem; padding: .22rem .75rem; margin-bottom: .5rem; }
  .home-products :deep(.section-title) { font-size: 1.15rem; }
  .home-products :deep(.section-sub) { font-size: .8rem; margin-top: .3rem; }

  /* Turn the grid into a swipeable row of app cards (peek of the next). */
  .hp-grid {
    display: flex;
    grid-template-columns: none;
    gap: .75rem;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: .4rem;
  }
  .hp-grid::-webkit-scrollbar { display: none; }
  .hp-card {
    flex: 0 0 78%;
    max-width: 300px;
    scroll-snap-align: start;
    border-radius: 16px;
  }
  .hp-card:hover { transform: none; box-shadow: none; }
  .hp-skel { flex: 0 0 78%; max-width: 300px; height: 210px; }
  .hp-card__body { padding: .8rem .9rem 1rem; }
  .hp-card__title { font-size: .98rem; }
  .hp-card__desc { font-size: .8rem; margin-bottom: .7rem; -webkit-line-clamp: 2; }
  .hp-card__cta { padding: .45rem .75rem; font-size: .76rem; }
}
</style>
